import type { CollectionModel, IkeDraw } from '@ikegps/core-shared';
import { action, observable, type ObservableMap, makeObservable } from 'mobx';
import { Services } from '../services';
import { CollectionModelStateEdit } from '../store/collection.edit.model';
import type { IkeState } from './ike.state';
import { CurrentState } from './page.state';

export class CollectionEditState implements IkeState {
	collectionRevision: string | null = null;

	protected editState: CollectionModelStateEdit;

	collection: CollectionModel | null = null;

	draw: ObservableMap<string, IkeDraw> = observable.map<string, IkeDraw>({}, { deep: false });

	constructor(collection: CollectionModel | null) {
		makeObservable(this);

		if (collection == null) {
			this.collectionRevision = null;
			return;
		}
		this.collectionRevision = collection.revision;
		this.editState = collection.beginEdit((state) => new CollectionModelStateEdit(state));
		this.collection = collection;
	}

	@action reset(): void {
		this.editState?.reset();
	}

	@action revert(fieldId: string) {
		this.editState?.fields.delete(fieldId);
	}

	isFieldDirty(fieldId: string) {
		return this.editState?.fields.has(fieldId);
	}

	get isDirty(): boolean {
		return this.collectionRevision != null && this.editState?.isDirty;
	}

	get isEditing() {
		return this.collectionRevision != null;
	}

	get currentDraw(): IkeDraw | null {
		const inspectedCapture = CurrentState.inspectedCaptureId;
		if (inspectedCapture == null) {
			return null;
		}
		return this.getIkeDraw(inspectedCapture);
	}

	getIkeDraw(captureId: string): IkeDraw {
		let existing = this.draw.get(captureId);
		if (existing != null) {
			return existing;
		}

		if (!this.collection) {
			throw new Error('Collection must be set before getting IkeDraw from CollectionEditState');
		}

		const drawState = Services.DrawState.createIkeDraw(this.collection, captureId);
		this.draw.set(captureId, drawState);
		return drawState;
	}

	cancelLinkFields() {
		this.draw.forEach((draw) => draw.state.setLinkFields(null));
	}
}
